import { icon } from './icons';
import { t } from './i18n/i18n';
import { socket } from '../core/network/socket';
import { glitchOnce } from './glitch';
import { authStore } from '../core/state/auth-store';
import { fetchChatHistory } from './chat-api';
import { titleBadgeHtml } from './title-badge';
import type { ChatMessageView } from './chat-api';

/**
 * Persistent overlay module (not a GameTab — no mount(root), no tab), same
 * shape/lifecycle as screens/notifications/notifications.ts: started once
 * from the app entry point, independent of whichever tab is active, self-
 * appends to document.body.
 *
 * A small collapsed pill fixed to a bottom corner expands into a compact
 * world-chat panel: recent history fetched once on first open, live
 * messages appended via the shared `socket` singleton thereafter. Collapsed
 * by default.
 *
 * Event contract, confirmed against backend/src/game/chat/chat.gateway.ts +
 * chat.constants.ts (CHAT_EVENTS.MESSAGE = 'chat:message'):
 *   emit  'chat:message' { content: string }             — sender/channel (the one v1 GLOBAL channel) are resolved server-side; every
 *                                                            authenticated socket auto-joins GLOBAL_CHAT_ROOM on connect, no client join step
 *   on    'chat:message' ChatMessageView                  — broadcast to every socket in that room, including the sender ("no separate
 *                                                            ack-based echo needed, the frontend just renders on receipt" — chat.gateway.ts) —
 *                                                            so this widget does NOT optimistically render its own outgoing message, it
 *                                                            waits for the same broadcast everyone else gets
 */

const MAX_MESSAGES = 200;

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/** Reads the flattened `senderUsername` both the live broadcast and /me/chat/history use; `sender.username` is a harmless fallback, not a real case per the confirmed backend shape (see chat-api.ts). */
function resolveSenderName(msg: ChatMessageView): string {
  return msg.senderUsername ?? msg.sender?.username ?? t('chat.unknownSender');
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** Both `name` and `content` are player-authored text and MUST be escaped — this is the one thing rendered from other players with no server-side sanitization (ChatService.sendMessage only length-checks). */
function messageRowHtml(msg: ChatMessageView, ownId: string | undefined): string {
  const isOwn = !!ownId && msg.senderId === ownId;
  const name = escapeHtml(resolveSenderName(msg));
  const content = escapeHtml(msg.content);
  const time = formatTime(msg.createdAt);
  return `
    <div class="flex flex-col gap-0.5 ${isOwn ? 'items-end' : 'items-start'}" data-message-id="${msg.id}">
      <div class="flex items-baseline gap-1.5 ${isOwn ? 'flex-row-reverse' : ''}">
        <span class="text-[10px] font-heading ${isOwn ? 'text-gold' : 'text-purple-light'}">${name}</span>
        ${msg.senderTitle?.label ? titleBadgeHtml(msg.senderTitle, 'title-badge--chat') : ''}
        <span class="text-[9px] text-text-dim tabular-nums">${time}</span>
      </div>
      <div class="text-xs text-text leading-snug break-words max-w-[85%] px-2 py-1 rounded-lg ${isOwn ? 'bg-gold/15 border border-gold/30' : 'bg-surface-2 border border-border'}">${content}</div>
    </div>
  `;
}

export function initChatWidget(): () => void {
  let disposed = false;
  let expanded = false;
  let historyLoaded = false;
  let historyLoading = false;
  let messages: ChatMessageView[] = [];

  const wrapper = document.createElement('div');
  wrapper.id = 'chat-widget';
  wrapper.innerHTML = `
    <button type="button" id="chat-widget-pill" aria-label="${t('chat.openLabel')}"
      class="fixed right-3 z-40 bottom-[calc(64px+env(safe-area-inset-bottom)+96px)] min-h-11 min-w-11 px-3 flex items-center gap-1.5 rounded-full bg-surface/80 border border-border text-text-dim hover:text-gold hover:border-gold transition-colors duration-[120ms] touch-manipulation">
      <span class="text-lg leading-none">${icon('chat')}</span>
      <span class="text-xs font-heading uppercase tracking-wide">${t('chat.worldChat')}</span>
      <span data-role="unread-dot" class="hidden w-2 h-2 rounded-full bg-crimson" aria-hidden="true"></span>
    </button>
    <section id="chat-widget-panel" aria-label="${t('chat.panelLabel')}"
      class="hidden fixed right-3 left-3 sm:left-auto sm:w-80 z-50 bottom-[calc(env(safe-area-inset-bottom)+8px)] h-[min(420px,70vh)] flex flex-col bg-surface border border-border border-t-2 border-t-gold rounded-xl panel-glow overflow-hidden">
      <header class="shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b border-border">
        <h2 class="font-heading text-xs text-gold uppercase tracking-wide">${t('chat.worldChat')}</h2>
        <button type="button" data-role="collapse" aria-label="${t('chat.closeLabel')}"
          class="min-w-11 min-h-11 -m-2 flex items-center justify-center text-text-dim hover:text-text leading-none text-xl touch-manipulation">&times;</button>
      </header>
      <div data-role="messages" class="flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 py-2 flex flex-col gap-1.5"></div>
      <form data-role="composer" class="shrink-0 flex items-center gap-2 p-2 border-t border-border">
        <input data-role="input" type="text" maxlength="500" autocomplete="off" placeholder="${t('chat.placeholder')}" aria-label="${t('chat.inputLabel')}"
          class="flex-1 min-h-11 min-w-0 px-3 bg-surface-2 border border-border rounded text-text text-sm placeholder:text-text-dim touch-manipulation" />
        <button type="submit" aria-label="${t('chat.sendLabel')}"
          class="font-button shrink-0 min-h-11 px-3 bg-gold text-bg text-sm rounded transition-colors duration-[120ms] hover:bg-gold-bright touch-manipulation">${t('chat.send')}</button>
      </form>
    </section>
  `;
  document.body.appendChild(wrapper);

  const pill = wrapper.querySelector('#chat-widget-pill') as HTMLButtonElement;
  const unreadDot = wrapper.querySelector('[data-role="unread-dot"]') as HTMLSpanElement;
  const panel = wrapper.querySelector('#chat-widget-panel') as HTMLElement;
  const messagesEl = wrapper.querySelector('[data-role="messages"]') as HTMLDivElement;
  const form = wrapper.querySelector('[data-role="composer"]') as HTMLFormElement;
  const input = wrapper.querySelector('[data-role="input"]') as HTMLInputElement;
  const collapseBtn = wrapper.querySelector('[data-role="collapse"]') as HTMLButtonElement;

  function ownId(): string | undefined {
    return authStore.get().user?.id ?? undefined;
  }

  function scrollToBottom(): void {
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  function renderAllMessages(): void {
    const id = ownId();
    messagesEl.innerHTML = messages.map((m) => messageRowHtml(m, id)).join('');
  }

  function appendMessageDom(msg: ChatMessageView): void {
    messagesEl.insertAdjacentHTML('beforeend', messageRowHtml(msg, ownId()));
  }

  /** Caps in-memory + rendered history so a long-lived session doesn't grow the DOM unbounded. */
  function trimMessages(): void {
    while (messages.length > MAX_MESSAGES) {
      messages.shift();
      messagesEl.firstElementChild?.remove();
    }
  }

  function markUnread(): void {
    unreadDot.classList.remove('hidden');
  }
  function clearUnread(): void {
    unreadDot.classList.add('hidden');
  }

  /**
   * Fetches once per widget lifetime, on first open — but only a SUCCESSFUL
   * fetch latches `historyLoaded`; a failure leaves it false so the next
   * open retries instead of leaving history permanently unavailable for the
   * rest of the session. `historyLoading` is the separate in-flight guard
   * that prevents two concurrent fetches if the panel is closed and
   * reopened before the first one resolves.
   *
   * Prepends (not appends) fetched history ahead of whatever's already in
   * `messages` — the socket listener is live from init, so a live message
   * can land while this fetch is in flight, and it must end up ordered
   * AFTER history once it resolves. Failure is non-fatal: the panel still
   * opens and live messages still work, it just starts from whatever
   * arrives from here on (until the next open retries).
   */
  async function ensureHistoryLoaded(): Promise<void> {
    if (historyLoaded || historyLoading || disposed) return;
    historyLoading = true;
    try {
      const history = await fetchChatHistory();
      if (disposed) return;
      const ordered = [...history].reverse(); // getHistory orders newest-first; render wants oldest-first
      const existingIds = new Set(messages.map((m) => m.id));
      const toPrepend = ordered.filter((m) => !existingIds.has(m.id));
      messages = [...toPrepend, ...messages];
      trimMessages();
      renderAllMessages();
      scrollToBottom();
      historyLoaded = true;
    } catch (err) {
      console.error('[chat-widget] failed to load history', err);
      if (messages.length === 0) {
        messagesEl.innerHTML = `<p class="text-xs text-text-dim text-center py-4">${t('chat.historyUnavailable')}</p>`;
      }
    } finally {
      historyLoading = false;
    }
  }

  function expand(): void {
    if (disposed || expanded) return;
    expanded = true;
    panel.classList.remove('hidden');
    pill.classList.add('hidden');
    clearUnread();
    void ensureHistoryLoaded();
    // Re-sync the DOM with `messages` on every open, not just the first.
    // Messages that arrive while collapsed are pushed into `messages` by
    // onChatMessage() but deliberately NOT painted into the DOM until the
    // panel is visible again (see onChatMessage below) — without this call,
    // history's own renderAllMessages() (inside ensureHistoryLoaded) is the
    // ONLY thing that ever paints the buffer, and it fires at most once per
    // widget lifetime. Every later open would otherwise silently show a
    // stale DOM while `messages` (and the unread dot's underlying backlog)
    // kept growing under it.
    renderAllMessages();
    scrollToBottom();
    input.focus();
  }

  function collapse(): void {
    if (!expanded) return;
    expanded = false;
    panel.classList.add('hidden');
    pill.classList.remove('hidden');
  }

  function onChatMessage(msg: ChatMessageView): void {
    if (disposed) return;
    if (messages.some((m) => m.id === msg.id)) return;
    messages.push(msg);
    trimMessages();
    // Someone else's message tears the widget hard — your own echo does not.
    if (!ownId() || msg.senderId !== ownId()) glitchOnce(expanded ? panel : pill, 'hard');
    if (expanded) {
      appendMessageDom(msg);
      scrollToBottom();
    } else {
      markUnread();
    }
  }
  socket.on('chat:message', onChatMessage);

  function onSubmit(e: Event): void {
    e.preventDefault();
    if (disposed) return;
    const value = input.value.trim().slice(0, 500);
    if (!value) return;
    input.value = '';
    socket.emit('chat:message', { content: value });
  }
  form.addEventListener('submit', onSubmit);

  pill.addEventListener('click', expand);
  collapseBtn.addEventListener('click', collapse);

  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && expanded) collapse();
  }
  document.addEventListener('keydown', onKeydown);

  return function teardown(): void {
    disposed = true;
    socket.off('chat:message', onChatMessage);
    document.removeEventListener('keydown', onKeydown);
    wrapper.remove();
  };
}
